import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BadgeCheck,
  Building2,
  CreditCard,
  FileCheck,
  Loader2,
  ShieldCheck,
  User,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/shared";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useMemberProfile, useSubmitKycMutation } from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import { kycStatusMeta } from "@/lib/account-profile-utils";
import { CustomerKycStatus } from "@/types/customer.dto";

const emptyKycForm = {
  fullName: "",
  aadhaarNumber: "",
  panNumber: "",
  bankAccountName: "",
  bankAccountNumber: "",
  bankIfsc: "",
  bankName: "",
};

const AccountKyc = () => {
  const { toast } = useToast();
  const { data: memberProfile, isLoading } = useMemberProfile();
  const submitKyc = useSubmitKycMutation();
  const [kycForm, setKycForm] = useState(emptyKycForm);

  useEffect(() => {
    if (!memberProfile?.kycDetails) return;
    setKycForm({
      fullName: memberProfile.kycDetails.fullName,
      aadhaarNumber: memberProfile.kycDetails.aadhaarNumber,
      panNumber: memberProfile.kycDetails.panNumber,
      bankAccountName: memberProfile.kycDetails.bankAccountName,
      bankAccountNumber: memberProfile.kycDetails.bankAccountNumber,
      bankIfsc: memberProfile.kycDetails.bankIfsc,
      bankName: memberProfile.kycDetails.bankName,
    });
  }, [memberProfile?.kycDetails]);

  const kycMeta = kycStatusMeta[memberProfile?.kycStatus ?? CustomerKycStatus.NotSubmitted];
  const canEdit =
    !memberProfile?.kycVerified && memberProfile?.kycStatus !== CustomerKycStatus.Submitted;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await submitKyc.mutateAsync(kycForm);
      toast({
        title: "KYC submitted",
        description: "We'll review your documents and notify you once verified.",
      });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <PageHeader
          title="KYC Verification"
          description="Submit your identity and bank details for wallet withdrawals and Member ID activation."
        />
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="KYC Verification"
        description="Submit your identity and bank details for wallet withdrawals and Member ID activation."
      />

      {/* Status banner */}
      <div className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold text-foreground">Verification status</p>
              <Badge variant={kycMeta.variant}>{kycMeta.label}</Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{kycMeta.description}</p>
          </div>
        </div>
        {memberProfile?.kycVerified && (
          <Button variant="outline" size="sm" asChild>
            <Link to="/account/member-id">
              <BadgeCheck className="mr-1.5 h-4 w-4" />
              Activate Member ID
            </Link>
          </Button>
        )}
      </div>

      {memberProfile?.kycRejectionReason && (
        <Alert variant="destructive">
          <AlertDescription>
            <strong>Rejection reason:</strong> {memberProfile.kycRejectionReason}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <User className="h-5 w-5 text-primary" />
              Identity details
            </CardTitle>
            <CardDescription>Must match your government-issued ID exactly.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="fullName">Full name (as per Aadhaar)</Label>
              <Input
                id="fullName"
                className="h-11"
                value={kycForm.fullName}
                disabled={!canEdit}
                onChange={(e) => setKycForm({ ...kycForm, fullName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="aadhaar">Aadhaar number</Label>
              <Input
                id="aadhaar"
                className="h-11 font-mono"
                placeholder="XXXX XXXX XXXX"
                value={kycForm.aadhaarNumber}
                disabled={!canEdit}
                onChange={(e) => setKycForm({ ...kycForm, aadhaarNumber: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pan">PAN number</Label>
              <Input
                id="pan"
                className="h-11 font-mono uppercase"
                placeholder="ABCDE1234F"
                value={kycForm.panNumber}
                disabled={!canEdit}
                onChange={(e) => setKycForm({ ...kycForm, panNumber: e.target.value.toUpperCase() })}
                required
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-border shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building2 className="h-5 w-5 text-primary" />
              Bank account
            </CardTitle>
            <CardDescription>For wallet withdrawals after KYC approval.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="bankName">Bank name</Label>
              <Input
                id="bankName"
                className="h-11"
                value={kycForm.bankName}
                disabled={!canEdit}
                onChange={(e) => setKycForm({ ...kycForm, bankName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="accountName">Account holder name</Label>
              <Input
                id="accountName"
                className="h-11"
                value={kycForm.bankAccountName}
                disabled={!canEdit}
                onChange={(e) => setKycForm({ ...kycForm, bankAccountName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="accountNumber">Account number</Label>
              <Input
                id="accountNumber"
                className="h-11 font-mono"
                value={kycForm.bankAccountNumber}
                disabled={!canEdit}
                onChange={(e) => setKycForm({ ...kycForm, bankAccountNumber: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ifsc">IFSC code</Label>
              <Input
                id="ifsc"
                className="h-11 font-mono uppercase"
                placeholder="SBIN0001234"
                value={kycForm.bankIfsc}
                disabled={!canEdit}
                onChange={(e) => setKycForm({ ...kycForm, bankIfsc: e.target.value.toUpperCase() })}
                required
              />
            </div>
          </CardContent>
        </Card>

        {canEdit && (
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" asChild>
              <Link to="/account/profile">Cancel</Link>
            </Button>
            <Button type="submit" disabled={submitKyc.isPending} className="gap-2">
              {submitKyc.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <FileCheck className="h-4 w-4" />
                  Submit for verification
                </>
              )}
            </Button>
          </div>
        )}

        {!canEdit && memberProfile?.kycVerified && (
          <Alert className="border-success/30 bg-success/5">
            <CreditCard className="h-4 w-4 text-success" />
            <AlertDescription className="text-foreground">
              Your KYC is verified. You can now{" "}
              <Link to="/account/member-id" className="font-medium text-primary hover:underline">
                activate your Member ID
              </Link>
              .
            </AlertDescription>
          </Alert>
        )}

        {!canEdit && memberProfile?.kycStatus === CustomerKycStatus.Submitted && (
          <Alert>
            <AlertDescription>
              Your documents are under review. You&apos;ll be notified once verification is complete.
            </AlertDescription>
          </Alert>
        )}
      </form>
    </div>
  );
};

export default AccountKyc;
